/**
 * Sponsors for one event.
 *
 * Adding a sponsor emails them an upload link immediately, with two exceptions
 * the screen says out loud rather than leaving staff to discover:
 *
 *   A DRAFT EVENT sends nothing. The sponsorship and its link are created and
 *   the invitation waits until somebody sets the event Active and invites from
 *   this screen. Filtering by Not invited finds who is still waiting.
 *
 *   EMAIL NOT CONFIGURED means the record and token are still created and the
 *   link is handed back once so staff can pass it on by hand. Better than
 *   blocking the feature on an integration, and better than pretending an email
 *   went out.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { t } from '../lib/copy';
import { T } from '../lib/copyEdit.tsx';


interface Contact {
  id: string;
  name: string;
  email: string;
  isPrimary: boolean;
}

/** What the table knows about a sponsor's upload link. Never the link itself:
 *  only its hash is stored, so a lost link is reissued, not recovered. */
interface LinkState {
  expiresAt: string;
  lastUsedAt: string | null;
  expired: boolean;
}

interface SponsorCounts {
  requested: number;
  submitted: number;
  approved: number;
  needsReview: number;
  fixRequested: number;
}

/** Derived server-side on every read, so it can never disagree with the
 *  submissions that determine it. */
interface SponsorState {
  band: 0 | 1 | 2;
  label: string;
  tone: 'warn' | 'info' | 'ok' | 'neutral';
  action: 'Review' | 'Invite' | 'Open';
}

interface SponsorRow {
  eventSponsorId: string;
  sponsorId: string;
  companyName: string;
  website: string | null;
  appIconData: string | null;
  tierTitle: string | null;
  invitedAt: string | null;
  status: 'invited' | 'not_invited';
  link: LinkState | null;
  counts: SponsorCounts;
  state: SponsorState;
  /** When they next hear from us, or why they will not. */
  reminder: { at: string | null; label: string; reason: string | null };
  /** Set once the sponsorship has been withdrawn, with why if anybody said. */
  withdrawnAt: string | null;
  withdrawnReason: string | null;
  /** Any message to this sponsor has bounced. Worth knowing before deciding
   *  they are ignoring you. */
  emailBounced: boolean;
  contacts: Contact[];
}

/** The status buckets the filter offers. Empty string is "all". */
type StatusFilter = '' | 'review' | 'outstanding' | 'approved' | 'not_invited' | 'withdrawn';

/**
 * Whether a row belongs in the chosen bucket.
 *
 * BUILT ON `state.action` AND `state.tone`, NOT ON THE LABEL. The label is
 * generated text — "3 need review", "1 to re-send" — so matching on it would
 * break the moment a count changed. Action and tone are the stable enums the
 * badge itself is derived from, which is also what makes the filter agree with
 * the Status column beside it: filtering to "Needs review" returns exactly the
 * rows whose badge says so.
 */
function matchesStatus(row: SponsorRow, filter: StatusFilter): boolean {
  switch (filter) {
    case '':
      return true;
    case 'review':
      return row.state.action === 'Review';
    case 'approved':
      return row.state.tone === 'ok';
    case 'not_invited':
      return row.state.action === 'Invite';
    case 'withdrawn':
      return row.withdrawnAt !== null;
    case 'outstanding':
      /* Invited and has sent nothing at all. Excludes the never-invited, who
       * have their own bucket and a different next step: chasing somebody who
       * was never asked is the complaint this app exists to prevent. */
      return row.state.action !== 'Invite' && row.counts.submitted === 0;
    default:
      return true;
  }
}

/**
 * Whole calendar days from today to that instant, in the viewer's own zone.
 *
 * Calendar days rather than elapsed hours, because that is how the answer gets
 * used. A reminder going out at 9am tomorrow is "tomorrow" whether it is now
 * 8am or 8pm; dividing the gap by 24 would call the same reminder "in 1 day"
 * from one and "today" from the other.
 */
function daysFromToday(iso: string): number {
  const then = new Date(iso);
  const now = new Date();
  const a = Date.UTC(then.getFullYear(), then.getMonth(), then.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a - b) / 86_400_000);
}

/**
 * The reminder, as one short line under the Status badge.
 *
 * IT NO LONGER HAS A COLUMN, and losing it is the fix rather than a saving.
 * The column held two different kinds of value: a date, for the sponsors a
 * reminder is actually coming for, and a phrase about why not for everybody
 * else. Nothing could be compared down it, which is the only thing a column
 * is for. Under the badge it reads as what it has always been, which is a
 * consequence of the status beside it rather than an independent fact.
 *
 * IT LEADS WITH HOW SOON, not with the date. "In 3 days" is what is being
 * judged; the date is the detail wanted after deciding to act, so it moves to
 * the hover title and to the sponsor's own reminders panel, which lists the
 * whole schedule rather than just the next step.
 *
 * `flag` marks the reasons somebody may need to act on, which is the same set
 * the old column coloured amber. A finished sponsor is not a warning.
 */
function reminderLine(reminder: SponsorRow['reminder']): { text: string; flag: boolean } {
  if (reminder.at) {
    const days = daysFromToday(reminder.at);
    if (days <= 0) return { text: 'Reminder today', flag: false };
    if (days === 1) return { text: 'Reminder tomorrow', flag: false };
    return { text: `Reminder in ${days} days`, flag: false };
  }

  switch (reminder.reason) {
    case 'waiting on our review':
      return { text: 'Paused, with you', flag: true };
    case 'held after a manual message':
      return { text: 'Held, you messaged', flag: false };
    case 'chaser paused':
      return { text: 'Reminders off', flag: true };
    case 'everything approved':
      return { text: 'Reminders finished', flag: false };
    case 'deadline passed':
    case 'schedule finished':
      return { text: 'Schedule ended', flag: true };
    /* Not "Not invited", which the badge beside it already says. What is worth
       adding is the consequence: nothing is going to chase them by itself. */
    case 'never invited':
      return { text: 'None until invited', flag: false };
    /* A reminder does not follow an invitation the same evening. Said on the
       row so a late-added sponsor does not look like one nothing is chasing. */
    case 'just invited':
      return { text: 'Just invited', flag: false };
    case 'no deadline set':
      return { text: 'No deadline set', flag: false };
    /* Scheduled, but the event has no deadline to count back from, so there is
       no date to give. Says nothing rather than inventing a reassurance. */
    default:
      return { text: '', flag: false };
  }
}

/**
 * Above this many requested versions the pips stop being countable at a glance
 * and turn back into the texture they were meant to replace, so past it the
 * count carries the cell on its own.
 */
const MAX_PIPS = 8;

const TONE: Record<SponsorState['tone'], string> = {
  warn: 'b-warning',
  info: 'b-info',
  ok: 'b-success',
  neutral: 'b-neutral',
};

/** Two letters on the sponsor's own colour, when there is no icon. Not a
 *  lesser state: a sponsor added without a website will never have one. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export default function Sponsors({ clientId, eventId }: { clientId: string; eventId: string }) {
  const [rows, setRows] = useState<SponsorRow[] | null>(null);
  const [canSendInvites, setCanSendInvites] = useState(true);
  /** Null until loaded, so the draft banner never flashes on a live event. */
  const [eventStatus, setEventStatus] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Shown once, after adding, when email is unavailable. Never refetchable. */
  const [handoffUrl, setHandoffUrl] = useState<{ company: string; url: string } | null>(null);
  /** Which sponsor a confirmation is open for, or null. */
  const [withdrawing, setWithdrawing] = useState<SponsorRow | null>(null);
  const [deleting, setDeleting] = useState<SponsorRow | null>(null);
  /** Which row is mid-invite, so only that button says "Sending". */
  const [inviting, setInviting] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');

  const base = `/api/clients/${clientId}/events/${eventId}/sponsors`;

  const load = useCallback(async () => {
    try {
      const res = await fetch(base);
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as {
        sponsors: SponsorRow[];
        canSendInvites: boolean;
        eventStatus: string | null;
      };
      setRows(body.sponsors);
      setCanSendInvites(body.canSendInvites);
      setEventStatus(body.eventStatus);
    } catch {
      setError('Could not load sponsors.');
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Invites one sponsor from their row.
   *
   * Reloads afterwards rather than patching the row locally, because the
   * server decides what the row now says: a successful send changes the state
   * to Open, and carrying artwork over from the library can change the
   * progress counts at the same time.
   */
  /**
   * Puts a withdrawn sponsor back.
   *
   * No confirmation: it undoes something rather than destroying anything, and
   * withdrawing again is one click away. Reminders resume from where the
   * schedule says they should be, not from the first step.
   */
  async function reinstate(eventSponsorId: string) {
    setError(null);
    try {
      const res = await fetch(`${base}/${eventSponsorId}/reinstate`, { method: 'POST' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? 'That could not be undone.');
        return;
      }
      void load();
    } catch {
      setError('That could not be undone. Check your connection and try again.');
    }
  }

  async function invite(eventSponsorId: string, companyName: string) {
    setInviting(eventSponsorId);
    setError(null);
    try {
      const res = await fetch(`${base}/${eventSponsorId}/invite`, { method: 'POST' });
      const body = (await res.json().catch(() => ({}))) as {
        sent?: boolean;
        portalUrl?: string;
        error?: string;
      };

      if (!res.ok) {
        setError(body.error ?? 'That invite did not send.');
        return;
      }

      /* Email is not configured or the send failed. The link is real and was
         issued either way, so it is handed over once for staff to pass on
         themselves rather than being silently lost. */
      if (!body.sent && body.portalUrl) {
        setHandoffUrl({ company: companyName, url: body.portalUrl });
      }

      await load();
    } catch {
      setError('That invite did not send. Check your connection and try again.');
    } finally {
      setInviting(null);
    }
  }

  if (error) return <p className="auth-error">{error}</p>;
  if (!rows) return <div className="app-loading" aria-busy="true" />;

  const needle = search.trim().toLowerCase();
  const filtering = needle !== '' || statusFilter !== '';

  /* Company name and contacts both, because "who is Dana?" is as likely a
     question as "where is Northgate?" — and a search that silently ignores the
     thing you typed reads as no results rather than as the wrong field. */
  const visible = rows.filter(
    (r) =>
      matchesStatus(r, statusFilter) &&
      (needle === '' ||
        r.companyName.toLowerCase().includes(needle) ||
        (r.tierTitle?.toLowerCase().includes(needle) ?? false) ||
        r.contacts.some(
          (c) =>
            c.name.toLowerCase().includes(needle) || c.email.toLowerCase().includes(needle),
        )),
  );

  return (
    <>
      {!canSendInvites && (
        <div className="notice">
          <b><T k="sponsors.notice.noEmail" d="Invitations are not sending yet." /></b> Email is not configured, so sponsors are recorded
          and their upload links created, but nothing is delivered. Each link is shown once after
          you add the sponsor so you can send it yourself.
        </div>
      )}

      {/* Shown before anybody adds a sponsor, because the question staff have
          at that moment is "will this email them?" — and until now the answer
          was yes, while two other screens said a draft could not reach
          sponsors. */}
      {eventStatus === 'draft' && (
        <div className="notice">
          <b>
            <T k="sponsors.notice.draft" d="This event is a draft, so nobody is being emailed." />
          </b>{' '}
          <T
            k="sponsors.notice.draftBody"
            d="You can add sponsors and set everything up, and no invitation goes out. Set the event to Active when you are ready, then invite them from this screen. Filter by Not invited to find who is still waiting."
          />
        </div>
      )}

      {handoffUrl && (
        <div className="notice notice--action">
          <b>Upload link for {handoffUrl.company}</b>
          <p className="fdesc" style={{ margin: '4px 0 8px' }}>
            Send this to them yourself. It is shown once and cannot be retrieved again, because
            only its hash is stored. Any earlier link for this sponsor has stopped working.
          </p>
          <div className="handoff">
            <input className="fi" readOnly value={handoffUrl.url} onFocus={(e) => e.target.select()} />
            <button
              type="button"
              className="btn-compact btn-compact--quiet"
              onClick={() => void navigator.clipboard?.writeText(handoffUrl.url)}
            >
              <T k="common.action.copy" d="Copy" />
            </button>
            <button type="button" className="btn-compact btn-compact--quiet" onClick={() => setHandoffUrl(null)}>
              <T k="common.action.done" d="Done" />
            </button>
          </div>
        </div>
      )}

      {/* ONE TOOLBAR, which is how the prototype draws this and is the
          difference between a controls row and three stacked bands. The count
          sat in its own header above, Add Sponsor beside it, and search and the
          filter in a separate strip below — so the page opened with two rows of
          furniture before any sponsor appeared. The count moves under the
          table, where the prototype also puts it. */}
      {!adding && rows.length > 0 && (
        <div className="toolbar">
          {/* Search and filter earn their place only once there is something
              to sift. One sponsor does not need either. */}
          {rows.length > 1 && (
            <>
              <div className="search">
                <svg className="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
                <input
                  className="search-input"
                  placeholder={t('sponsors.search.placeholder', 'Search sponsors…')}
                  aria-label={t('sponsors.search.label', 'Search sponsors')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <label className="fpill">
                <span className="fpill-label"><T k="sponsors.filter.heading" d="Status" /></span>
                <select
                  className="fpill-select"
                  value={statusFilter}
                  aria-label="Filter by status"
                  onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                >
                  <option value="">{t('sponsors.filter.all', 'All statuses')}</option>
                  <option value="review">{t('sponsors.filter.review', 'Needs review')}</option>
                  <option value="outstanding">{t('sponsors.filter.outstanding', 'Awaiting upload')}</option>
                  <option value="approved">{t('sponsors.filter.approved', 'Approved')}</option>
                  <option value="not_invited">{t('sponsors.filter.notInvited', 'Not invited yet')}</option>
                  <option value="withdrawn">{t('sponsors.filter.withdrawn', 'Withdrawn')}</option>
                </select>
              </label>
            </>
          )}

          <div className="toolbar-gap" />

          {/* A NEW TAB, not a takeover of this one. The sponsor's page belongs
              at full width with no admin shell around it, and keeping the
              sponsors table open behind it is what you want while checking:
              flip to the tab, look, flip back. Opens on the first sponsor and
              the tab lets you switch. */}
          <a
            className="btn-compact btn-compact--quiet"
            href={`/c/${clientId}/events/${eventId}/portal-preview`}
            target="_blank"
            rel="noreferrer"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
            </svg>
            <T k="sponsors.action.preview" d="Preview Sponsor Portal" />
          </a>

          <button type="button" className="btn-compact" onClick={() => setAdding(true)}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <T k="sponsors.action.add" d="Add Sponsor" />
          </button>
        </div>
      )}

      {withdrawing && (
        <WithdrawPanel
          base={base}
          row={withdrawing}
          onCancel={() => setWithdrawing(null)}
          onDone={() => {
            setWithdrawing(null);
            void load();
          }}
        />
      )}

      {deleting && (
        <DeletePanel
          base={base}
          row={deleting}
          onCancel={() => setDeleting(null)}
          onDone={() => {
            setDeleting(null);
            void load();
          }}
        />
      )}

      {adding ? (
        <AddSponsor
          base={base}
          live={eventStatus === 'active'}
          onCancel={() => setAdding(false)}
          onAdded={(handoff) => {
            setAdding(false);
            if (handoff) setHandoffUrl(handoff);
            void load();
          }}
        />
      ) : rows.length === 0 ? (
        <div className="empty">
          <p className="empty-title"><T k="sponsors.empty.title" d="No sponsors yet" /></p>
          <p className="empty-copy">
            Add sponsors and each one gets a branded upload link, so nobody has to be chased by
            hand.
          </p>
          {/* The toolbar is hidden until there is a list to act on, so the
              empty state carries the only way out of it. */}
          <button type="button" className="btn-compact" onClick={() => setAdding(true)}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add Sponsor
          </button>
        </div>
      ) : visible.length === 0 ? (
        /* Distinct from "no sponsors yet". There ARE sponsors; the filter is
           hiding them, and saying "none" here would read as data loss. */
        <div className="empty">
          <p className="empty-title"><T k="sponsors.noMatch.title" d="Nothing matches" /></p>
          <p className="empty-copy">
            No sponsor on this event matches that search or status. Clear the filters to see all{' '}
            {rows.length}.
          </p>
          <button
            type="button"
            className="btn-compact btn-compact--quiet"
            onClick={() => {
              setSearch('');
              setStatusFilter('');
            }}
          >
            <T k="sponsors.action.clearFilters" d="Clear Filters" />
          </button>
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl tbl--sponsors">
            <thead>
              <tr>
                <th><T k="sponsors.col.sponsor" d="Sponsor" /></th>
                <th><T k="sponsors.col.tier" d="Tier" /></th>
                <th><T k="sponsors.col.logos" d="Logos" /></th>
                <th><T k="sponsors.col.status" d="Status" /></th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                /* CONTACT IS NO LONGER A COLUMN. It was a name with no address
                   and no action, so it could not be used to write to anybody
                   and cost width the sponsor name needed. The sponsor's own
                   page carries every contact with their email. */
                const done = r.counts.requested > 0 && r.counts.approved >= r.counts.requested;
                const reminder = reminderLine(r.reminder);
                /* WHO TO WRITE TO, under the company they belong to. The
                   sponsor's name answers "which row", but the question that
                   follows it is almost always "who do I chase" — and that used
                   to mean opening the sponsor to find out.
                   Falls back to the first contact, because a row imported
                   without the primary flag set still has somebody on it, and
                   showing nobody would read as nobody on file. */
                const primary = r.contacts.find((c) => c.isPrimary) ?? r.contacts[0];
                /* The date the relative phrase is standing in for, on hover.
                   Written out in full, because a tooltip has the room and
                   "Friday, 19 September" cannot be misread the way 19/09 can. */
                const reminderOn = r.reminder.at
                  ? new Date(r.reminder.at).toLocaleDateString(undefined, {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                    })
                  : undefined;

                return (
                  <tr key={r.eventSponsorId} className={r.withdrawnAt ? 'sponsor--out' : undefined}>
                    <td>
                      <div className="cell">
                        {/* The mark is a second route to the same screen, for
                            anyone who aims at the logo rather than the words.
                            Hidden from assistive technology and skipped by the
                            keyboard, so it is not a duplicate tab stop
                            announcing the same destination twice. */}
                        <Link
                          className="fav-link"
                          to={`sponsors/${r.eventSponsorId}`}
                          tabIndex={-1}
                          aria-hidden="true"
                        >
                          {/* White ground for a fetched icon: favicons are
                              usually transparent, so a coloured tile behind
                              one can paint the mark in its own colour. */}
                          {r.appIconData ? (
                            <span className="fav fav--icon">
                              <img src={r.appIconData} alt="" />
                            </span>
                          ) : (
                            <span className="fav">{initials(r.companyName)}</span>
                          )}
                        </Link>
                        <div className="cell-text">
                          {/* ONE LINE, TRUNCATED, with the full name on hover
                              and available to a screen reader. Company names
                              here run to "Cornerstone Business Services of
                              Greater Chicago", and wrapping one made its row
                              twice the height of every other row, which is what
                              made the table hard to scan down. */}
                          <b>
                            <Link
                              className="tlink"
                              to={`sponsors/${r.eventSponsorId}`}
                              title={r.companyName}
                            >
                              {r.companyName}
                            </Link>
                          </b>
                          {/* The email is a mailto, not a label. A contact name
                              with no address was dropped from this table once
                              already for being unusable; putting it back
                              without the address would repeat that. Its click
                              is stopped from reaching the row so opening a mail
                              client does not also navigate away from the
                              table. */}
                          {primary && (
                            <>
                              <span className="cell-contact">{primary.name}</span>
                              <a
                                className="cell-email"
                                href={`mailto:${primary.email}`}
                                title={primary.email}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {primary.email}
                              </a>
                            </>
                          )}
                          {/* Only ever a problem, never machinery. A working
                              link belongs on the sponsor's own screen. */}
                          {r.emailBounced && <div className="csub link-warn"><T k="sponsors.flag.bounced" d="Email bounced" /></div>}
                          {r.link?.expired && <div className="csub link-warn"><T k="sponsors.flag.linkExpired" d="Link expired" /></div>}
                        </div>
                      </div>
                    </td>

                    {/* Its own column now. Tier is what somebody scans for
                        when deciding whose logo matters most, and buried as a
                        sub-line under the name it could not be compared down
                        the column. Truncated, because real values run to
                        "Exclusive Sponsor: E&O Insurance and Risk Management"
                        and this column mostly holds "Gold". */}
                    <td>
                      {r.tierTitle ? (
                        <span className="tier" title={r.tierTitle}>
                          {r.tierTitle}
                        </span>
                      ) : (
                        <span className="csub">—</span>
                      )}
                    </td>

                    {/* ONE PIP PER REQUESTED VERSION, which gives the column a
                        shape before it gives a number. It used to draw a
                        progress bar AND print the count: two encodings of one
                        figure in 130px, where a 5px bar is too small to read as
                        a proportion, so the bar cost width and gave nothing
                        back. Pips are countable at the three to five versions
                        an event actually asks for. */}
                    <td>
                      {r.counts.requested === 0 ? (
                        <span className="lg-none"><T k="sponsors.logos.noneRequested" d="None requested" /></span>
                      ) : (
                        <div className="pips">
                          {r.counts.requested <= MAX_PIPS && (
                            <span className="pip-row" aria-hidden="true">
                              {Array.from({ length: r.counts.requested }, (_, i) => (
                                <span
                                  key={i}
                                  className={
                                    i < r.counts.approved
                                      ? done
                                        ? 'pip is-all'
                                        : 'pip is-on'
                                      : 'pip'
                                  }
                                />
                              ))}
                            </span>
                          )}
                          {/* "2/4" is for the eye, next to pips that already
                              say it; the sentence beside it is what a screen
                              reader gets, because "two slash four" in a column
                              called Logos is a guess. */}
                          <span className="pip-n" aria-hidden="true">
                            {r.counts.approved}/{r.counts.requested}
                          </span>
                          <span className="visually-hidden">
                            {r.counts.approved} of {r.counts.requested} logos approved
                          </span>
                        </div>
                      )}
                    </td>

                    <td>
                      <span className={`badge ${TONE[r.state.tone]}`}>
                        <span className="d" />
                        {r.state.label}
                      </span>
                      {reminder.text && (
                        <div
                          className={reminder.flag ? 'rem-sub rem-sub--flag' : 'rem-sub'}
                          title={reminderOn}
                        >
                          {reminder.text}
                        </div>
                      )}
                      {/* Why they are out, where the reminder line would be.
                          The two never both apply: a withdrawn sponsor has no
                          next reminder, which is the point. */}
                      {r.withdrawnAt && r.withdrawnReason && (
                        <div className="rem-sub" title={r.withdrawnReason}>
                          {r.withdrawnReason}
                        </div>
                      )}
                    </td>

                    <td>
                      {/* Bordered like the Users table's actions, so the two
                          tables in the app behave the same way. */}
                      <div className="row-actions">
                        {/* INVITE IS A BUTTON, not a link, and that is the
                            fix. deriveState has always returned "Invite" as
                            this row's action, but it was rendered as a link to
                            the sponsor page, and the sponsor page had no
                            invite control on it. So a sponsor whose invite
                            failed to send showed an action that led nowhere
                            and could never be invited again. */}
                        {r.withdrawnAt ? (
                          <Link className="cedit" to={`sponsors/${r.eventSponsorId}`}>
                            <T k="common.action.open" d="Open" />
                          </Link>
                        ) : r.state.action === 'Invite' ? (
                          <button
                            type="button"
                            className="cedit"
                            disabled={inviting === r.eventSponsorId}
                            onClick={() => void invite(r.eventSponsorId, r.companyName)}
                          >
                            {inviting === r.eventSponsorId ? 'Sending…' : 'Invite'}
                          </button>
                        ) : (
                          <Link
                            className="cedit"
                            to={`sponsors/${r.eventSponsorId}${r.state.action === 'Review' ? '?tab=review' : ''}`}
                          >
                            {r.state.action}
                          </Link>
                        )}

                        {/* WITHDRAW AND DELETE LIVE BEHIND THE MENU, not on the
                            row. Neither is something anybody does often, and a
                            delete button sitting next to Review is a delete
                            button somebody eventually presses by mistake. */}
                        <RowMenu
                          row={r}
                          onWithdraw={() => setWithdrawing(r)}
                          onReinstate={() => void reinstate(r.eventSponsorId)}
                          onDelete={() => setDeleting(r)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* The count, under the table where the prototype puts it. It says what
          is SHOWN when a filter is on and what exists when it is not: a count
          that ignores the filter above it is the quickest way to make somebody
          think the filter is broken. */}
      {!adding && rows.length > 0 && visible.length > 0 && (
        <p className="tbl-foot">
          {filtering
            ? `Showing ${visible.length} of ${rows.length} sponsor${rows.length === 1 ? '' : 's'}`
            : `${rows.length} sponsor${rows.length === 1 ? '' : 's'}`}
        </p>
      )}
    </>
  );
}


/**
 * The link's state in words. Deliberately never the link.
 *
 * Only a hash of the token is stored, so there is nothing to reveal here even
 * if the UI wanted to — which is the point. Staff get enough to answer "does
 * this sponsor have a working link?" without the link becoming something that
 * can be shoulder-surfed off a screen or pulled out of a support screenshot.
 */
function describeLink(link: LinkState | null): string {
  if (!link) return 'None';
  if (link.expired) return 'Expired';
  const lapses = new Date(link.expiresAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  return link.lastUsedAt ? `Opened · until ${lapses}` : `Not opened · until ${lapses}`;
}

interface LibrarySponsor {
  id: string;
  companyName: string;
  website: string | null;
}

/**
 * The per-row menu.
 *
 * Holds the two actions that are rare and consequential, so neither sits where
 * a thumb lands on the way to Review. What it offers depends on the row: a
 * withdrawn sponsor is offered Reinstate instead of Withdraw, because the only
 * thing anybody wants from that row is to undo it.
 */
/**
 * Confirming a withdrawal.
 *
 * Says what will happen in the four lines somebody actually needs, because
 * "are you sure?" asks a question nobody can answer without knowing the
 * consequences. The reason is optional and shown on the row afterwards, so the
 * next person does not have to ask why a sponsor is sitting there inert.
 */
function WithdrawPanel({
  base,
  row,
  onCancel,
  onDone,
}: {
  base: string;
  row: SponsorRow;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function save() {
    setWorking(true);
    setProblem(null);
    try {
      const res = await fetch(`${base}/${row.eventSponsorId}/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() || undefined }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setProblem(body.error ?? 'That did not save.');
        return;
      }
      onDone();
    } catch {
      setProblem('That did not save. Check your connection and try again.');
    } finally {
      setWorking(false);
    }
  }

  return (
    <Sheet onClose={onCancel}>
      <div className="panel">
        <div className="panel-head">
          <h3>Mark {row.companyName} as Withdrawn</h3>
        </div>
        <div className="panel-body">
          <p className="fdesc" style={{ marginTop: 0 }}>
            <T
              k="sponsors.withdraw.intro"
              d="They stay on the event and keep their history. Nothing is deleted."
            />
          </p>

          <ul className="willlist">
            <li><T k="sponsors.withdraw.w1" d="Reminders stop immediately" /></li>
            <li><T k="sponsors.withdraw.w2" d="They drop out of the outstanding and pending review counts" /></li>
            <li><T k="sponsors.withdraw.w3" d="Their artwork stays in the library, under the company" /></li>
            <li><T k="sponsors.withdraw.w4" d="Their upload link keeps working, so reinstating needs no new email" /></li>
          </ul>

          <div className="fg">
            <label className="fl" htmlFor="withdraw-reason">
              <T k="sponsors.withdraw.reason" d="Reason · Optional" />
            </label>
            <input
              id="withdraw-reason"
              className="fi"
              maxLength={200}
              placeholder="Payment did not clear"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <p className="fdesc">
              <T
                k="sponsors.withdraw.reasonHint"
                d="Shown under their status, so the next person does not have to ask."
              />
            </p>
          </div>

          {problem && <p className="auth-error">{problem}</p>}

          <div className="factions">
            <button type="button" className="btn-compact btn-compact--quiet" onClick={onCancel}>
              <T k="common.action.cancel" d="Cancel" />
            </button>
            <button type="button" className="btn-compact" disabled={working} onClick={() => void save()}>
              {working ? 'Saving…' : 'Mark as Withdrawn'}
            </button>
          </div>
        </div>
      </div>
    </Sheet>
  );
}

/**
 * Confirming a delete.
 *
 * ASKS FOR THE NAME BACK when there is anything to lose, which is the bar
 * deleting an event already sets. A sponsorship with no uploads and no emails
 * is a mistake being tidied up and gets no ceremony — the server decides which
 * of the two this is, so the rule lives in one place.
 *
 * It also says plainly that withdrawing is usually the right answer, because
 * this dialog is where somebody is most likely to be about to burn a record
 * they will want later.
 */
function DeletePanel({
  base,
  row,
  onCancel,
  onDone,
}: {
  base: string;
  row: SponsorRow;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [confirmName, setConfirmName] = useState('');
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [impact, setImpact] = useState<{ uploads: number; emails: number } | null>(null);
  const [needsName, setNeedsName] = useState(false);

  async function remove() {
    setWorking(true);
    setProblem(null);
    try {
      const res = await fetch(`${base}/${row.eventSponsorId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(needsName ? { confirmName } : {}),
      });

      if (res.status === 409) {
        /* There is history. The server sends back what would go, and the name
           has to be typed before it will do it. */
        const body = (await res.json().catch(() => ({}))) as {
          impact?: { uploads: number; emails: number };
        };
        setImpact(body.impact ?? null);
        setNeedsName(true);
        return;
      }

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setProblem(body.error ?? 'That could not be deleted.');
        return;
      }
      onDone();
    } catch {
      setProblem('That could not be deleted. Check your connection and try again.');
    } finally {
      setWorking(false);
    }
  }

  /* Asked for as soon as the panel opens, so somebody sees what they are about
     to destroy before they reach for the button rather than after. */
  useEffect(() => {
    void remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once to
    // fetch the impact; the server refuses and reports rather than deleting.
  }, []);

  return (
    <Sheet onClose={onCancel}>
      <div className="panel">
        <div className="panel-head">
          <h3>Delete {row.companyName}</h3>
        </div>
        <div className="panel-body">
          {needsName ? (
            <>
              <p className="fdesc" style={{ marginTop: 0 }}>
                <b><T k="sponsors.delete.permanent" d="This cannot be undone." /></b>{' '}
                <T k="sponsors.delete.intro" d="Deleting removes them from this event along with:" />
              </p>
              <ul className="willlist">
                <li>
                  <b>{impact?.uploads ?? 0}</b>{' '}
                  <T k="sponsors.delete.uploads" d="uploads, and the review decisions on them" />
                </li>
                <li>
                  <b>{impact?.emails ?? 0}</b>{' '}
                  <T k="sponsors.delete.emails" d="emails of history, including what was sent and when" />
                </li>
                <li><T k="sponsors.delete.contacts" d="Their contacts and upload link" /></li>
              </ul>
              <p className="fdesc" style={{ color: 'var(--success)', fontWeight: 600 }}>
                <T
                  k="sponsors.delete.artwork"
                  d="Their artwork stays in the logo library. It belongs to the company, not to this event."
                />
              </p>
              <p className="fdesc">
                <T
                  k="sponsors.delete.prefer"
                  d="If they simply fell through, mark them withdrawn instead. That keeps the record of what was sent and when, which deleting destroys."
                />
              </p>

              <div className="fg">
                <label className="fl" htmlFor="confirm-name">
                  Type {row.companyName} to confirm
                </label>
                <input
                  id="confirm-name"
                  className="fi"
                  placeholder={row.companyName}
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                />
              </div>
            </>
          ) : (
            <p className="fdesc" style={{ marginTop: 0 }}>
              <T k="sponsors.delete.checking" d="Checking what this would remove…" />
            </p>
          )}

          {problem && <p className="auth-error">{problem}</p>}

          <div className="factions">
            <button type="button" className="btn-compact btn-compact--quiet" onClick={onCancel}>
              <T k="common.action.cancel" d="Cancel" />
            </button>
            <button
              type="button"
              className="btn-compact btn-compact--danger"
              disabled={working || (needsName && confirmName !== row.companyName)}
              onClick={() => void remove()}
            >
              {working ? 'Deleting…' : 'Delete Permanently'}
            </button>
          </div>
        </div>
      </div>
    </Sheet>
  );
}

/**
 * A panel that opens over the page.
 *
 * Same shape as the library's, and here for the same reason: a confirmation
 * rendered inline at the top of a long table is a confirmation nobody sees.
 */
function Sheet({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div className="sheet-wrap" role="dialog" aria-modal="true">
      <button type="button" className="sheet-scrim" aria-label="Close" onClick={onClose} />
      <div className="sheet">{children}</div>
    </div>
  );
}

function RowMenu({
  row,
  onWithdraw,
  onReinstate,
  onDelete,
}: {
  row: SponsorRow;
  onWithdraw: () => void;
  onReinstate: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  /** Where to draw the menu, in viewport coordinates. See the comment on
   *  `.rowmenu-list` in index.css for why it is not simply absolute. */
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);

  /*
   * MEASURED FROM THE BUTTON, because the menu is positioned against the
   * viewport rather than against the row.
   *
   * The table is wrapped in `.tbl-wrap`, which has `overflow: hidden` so the
   * rounded corners actually clip the table. An absolutely positioned menu is
   * clipped by that too, so on the last row the dropdown was cut off at the
   * bottom edge of the table.
   *
   * Flipped above the button when there is not room below, so the last row in
   * a long table opens upward rather than off the bottom of the window.
   */
  useEffect(() => {
    if (!open) return;

    function place() {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      /* Roughly the menu's height; exact enough to decide which way to open. */
      const needed = 150;
      const below = window.innerHeight - r.bottom;
      setAt({
        top: below < needed ? r.top - needed - 4 : r.bottom + 5,
        right: window.innerWidth - r.right,
      });
    }

    place();
    window.addEventListener('resize', place);
    /* Capture, so it follows the table's own scroller as well as the page. */
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  return (
    <div className={`rowmenu${open ? ' open' : ''}`} ref={ref}>
      <button
        type="button"
        ref={btnRef}
        className="kebab"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`More for ${row.companyName}`}
        onClick={() => setOpen((v) => !v)}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="5" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="12" cy="19" r="1.6" />
        </svg>
      </button>

      <div
        className="rowmenu-list"
        role="menu"
        style={at ? { top: at.top, right: at.right } : undefined}
      >
        {row.withdrawnAt ? (
          <button
            type="button"
            role="menuitem"
            className="rowmenu-item"
            onClick={() => {
              setOpen(false);
              onReinstate();
            }}
          >
            <b><T k="sponsors.menu.reinstate" d="Reinstate" /></b>
            <small>
              <T k="sponsors.menu.reinstateSub" d="Puts them back and restarts reminders" />
            </small>
          </button>
        ) : (
          <button
            type="button"
            role="menuitem"
            className="rowmenu-item"
            onClick={() => {
              setOpen(false);
              onWithdraw();
            }}
          >
            <b><T k="sponsors.menu.withdraw" d="Mark as Withdrawn" /></b>
            <small>
              <T k="sponsors.menu.withdrawSub" d="Stops reminders, keeps everything" />
            </small>
          </button>
        )}

        <div className="rowmenu-sep" />

        <button
          type="button"
          role="menuitem"
          className="rowmenu-item rowmenu-item--danger"
          onClick={() => {
            setOpen(false);
            onDelete();
          }}
        >
          <b><T k="sponsors.menu.delete" d="Delete Sponsor" /></b>
          <small><T k="sponsors.menu.deleteSub" d="Removes them from this event" /></small>
        </button>
      </div>
    </div>
  );
}

function AddSponsor({
  base,
  live,
  onCancel,
  onAdded,
}: {
  base: string;
  /** Whether adding this sponsor would email them. False on a draft, where
   *  nothing sends and offering the choice would describe a decision that is
   *  not being made. */
  live: boolean;
  onCancel: () => void;
  onAdded: (handoff: { company: string; url: string } | null) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /**
   * Whether to send the invitation now.
   *
   * DEFAULTS TO SENDING, because that is what adding a sponsor has always done
   * and is still the common case. The point of the choice is not to make
   * people think every time; it is that pressing Add can no longer email
   * somebody before the details have been checked.
   */
  const [invite, setInvite] = useState<'now' | 'later'>('now');

  const [companyName, setCompanyName] = useState('');
  const [linkTo, setLinkTo] = useState('');
  const [library, setLibrary] = useState<LibrarySponsor[]>([]);

  /* Additional contacts are all CC'd on every sponsor email and act as the
   * fallback if the primary goes quiet — typically a marketing coordinator
   * plus the designer who actually holds the artwork. */
  const [extra, setExtra] = useState<{ name: string; email: string }[]>([]);

  useEffect(() => {
    void fetch(`${base}/library`)
      .then((r) => (r.ok ? r.json() : { sponsors: [] }))
      .then((b: { sponsors: LibrarySponsor[] }) => setLibrary(b.sponsors))
      .catch(() => setLibrary([]));
  }, [base]);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSaving(true);
    setProblem(null);

    const form = new FormData(e.currentTarget);
    const res = await fetch(base, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        companyName: companyName.trim(),
        website: String(form.get('website') ?? '').trim() || undefined,
        tierTitle: String(form.get('tierTitle') ?? '').trim() || undefined,
        primaryContact: {
          name: String(form.get('contactName') ?? '').trim(),
          email: String(form.get('contactEmail') ?? '').trim(),
        },
        additionalContacts: extra.filter((c) => c.name.trim() && c.email.trim()),
        linkToSponsorId: linkTo || undefined,
        /* Only meaningful on a live event. A draft sends nothing either way,
           and the server holds that line regardless of what is sent here. */
        invite: live ? invite : undefined,
      }),
    });

    setSaving(false);

    if (res.ok) {
      const body = (await res.json()) as { portalUrl: string | null };
      onAdded(body.portalUrl ? { company: companyName.trim(), url: body.portalUrl } : null);
      return;
    }

    const body = (await res.json().catch(() => ({}))) as { error?: string };
    setProblem(body.error ?? 'That did not save. Check the fields and try again.');
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="panel-head">
        <h3><T k="sponsors.form.title" d="Add Sponsor" /></h3>
        <span className="hint">they receive an upload link for this event</span>
      </div>

      <div className="panel-body">
        {library.length > 0 && (
          <div className="fg">
            <label className="fl" htmlFor="linkTo"><T k="sponsors.form.returning" d="Returning sponsor" /></label>
            <select
              id="linkTo"
              className="fi"
              value={linkTo}
              onChange={(e) => {
                setLinkTo(e.target.value);
                const found = library.find((l) => l.id === e.target.value);
                if (found) setCompanyName(found.companyName);
              }}
            >
              <option value="">New sponsor</option>
              {library.map((l) => (
                <option key={l.id} value={l.id}>{l.companyName}</option>
              ))}
            </select>
            <p className="fdesc">
              Linking to an existing company keeps their logo history together across events.
            </p>
          </div>
        )}

        <div className="frow2">
          <div className="fg">
            <label className="fl" htmlFor="companyName"><T k="common.field.companyName" d="Company Name" /></label>
            <input
              id="companyName"
              required
              maxLength={200}
              className="fi"
              placeholder="Sunbelt Network"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              disabled={linkTo !== ''}
            />
          </div>
          <div className="fg">
            <label className="fl" htmlFor="tierTitle"><T k="common.field.tier" d="Tier / Title" /></label>
            <input
              id="tierTitle"
              name="tierTitle"
              maxLength={200}
              className="fi"
              placeholder="Gold"
            />
            <p className="fdesc">Free text. "Exclusive Sponsor: E&amp;O Insurance" works fine.</p>
          </div>
        </div>

        {!linkTo && (
          <div className="fg">
            <label className="fl" htmlFor="website"><T k="common.field.website" d="Website" /></label>
            <input id="website" name="website" className="fi" placeholder="sunbelt.com" />
          </div>
        )}

        <div className="frow2">
          <div className="fg">
            <label className="fl" htmlFor="contactName"><T k="sponsors.form.contactName" d="Primary Contact Name" /></label>
            <input id="contactName" name="contactName" required className="fi" placeholder="Marcus Hill" />
          </div>
          <div className="fg">
            <label className="fl" htmlFor="contactEmail"><T k="common.field.contactEmail" d="Contact Email" /></label>
            <input
              id="contactEmail"
              name="contactEmail"
              type="email"
              required
              className="fi"
              placeholder="m.hill@sunbelt.com"
            />
          </div>
        </div>

        <div className="fg">
          <label className="fl">
            Additional Contacts <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>· optional</span>
          </label>

          {extra.map((c, i) => (
            <div key={i} className="fetch-row" style={{ marginBottom: 8 }}>
              <input
                className="fi"
                placeholder="Name"
                value={c.name}
                onChange={(e) =>
                  setExtra((prev) => prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))
                }
              />
              <input
                className="fi"
                type="email"
                placeholder="Email"
                value={c.email}
                onChange={(e) =>
                  setExtra((prev) => prev.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))
                }
              />
              <button
                type="button"
                className="btn-compact btn-compact--quiet"
                onClick={() => setExtra((prev) => prev.filter((_, j) => j !== i))}
                aria-label="Remove contact"
              >
                ✕
              </button>
            </div>
          ))}

          <button
            type="button"
            className="btn-compact btn-compact--quiet"
            onClick={() => setExtra((prev) => [...prev, { name: '', email: '' }])}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <T k="sponsors.form.addContact" d="Add Another Contact" />
          </button>
          <p className="fdesc">
            Everyone here is CC'd on the sponsor's emails and can pick things up if the primary goes
            quiet.
          </p>
        </div>

        {/* THE CHOICE, directly above the button that acts on it.
            Only on a live event: a draft sends nothing whatever is picked, and
            offering a decision that is not being made would be worse than
            offering none. Adding a sponsor used to email them the instant you
            pressed the button, with no warning and nothing to undo. */}
        {live && (
          <fieldset className="invopt">
            <legend className="fl">
              <T k="sponsors.form.inviteLegend" d="The invitation" />
            </legend>

            <label className="invopt-row">
              <input
                type="radio"
                name="invite"
                value="now"
                checked={invite === 'now'}
                onChange={() => setInvite('now')}
              />
              <span>
                <b><T k="sponsors.form.inviteNow" d="Send it now" /></b>
                <span className="invopt-sub">
                  <T
                    k="sponsors.form.inviteNowSub"
                    d="They get their upload link as soon as you add them."
                  />
                </span>
              </span>
            </label>

            <label className="invopt-row">
              <input
                type="radio"
                name="invite"
                value="later"
                checked={invite === 'later'}
                onChange={() => setInvite('later')}
              />
              <span>
                <b><T k="sponsors.form.inviteLater" d="Add without emailing" /></b>
                <span className="invopt-sub">
                  <T
                    k="sponsors.form.inviteLaterSub"
                    d="Check the details or upload artwork for them first, then invite from this screen. They show as Not invited until you do."
                  />
                </span>
              </span>
            </label>
          </fieldset>
        )}

        {problem && <p className="auth-error">{problem}</p>}

        <div className="factions">
          <button type="button" className="btn-compact btn-compact--quiet" onClick={onCancel}>
            <T k="common.action.cancel" d="Cancel" />
          </button>
          <button type="submit" className="btn-compact" disabled={saving}>
            {saving ? 'Adding…' : live && invite === 'later' ? 'Add Sponsor' : 'Add & Send Invite'}
          </button>
        </div>
      </div>
    </form>
  );
}
